import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { PeriodSelector } from '../PeriodSelector/PeriodSelector';
import type { PeriodValue } from '../../lib/period';
import { GAP_FILLER, gapTriageBanner, liveGaps } from '../../gap-filler/mockData';
import {
  GF_OUTCOME_LABEL,
  GF_SOURCE_LABEL,
} from '../../gap-filler/types';
import type { GfFunnel, GfGap, GfOutcome, GfSource } from '../../gap-filler/types';

export interface GapFillerViewProps {
  /** Whether Gap Filler is on hold — owned above this component (§2). */
  onHold: boolean;
  onAddGap?: () => void;
  onOpenSettings?: () => void;
  onSetHold?: (onHold: boolean) => void;
  /** The report period (spec 41) — the selector sits first in the header. */
  period?: PeriodValue;
  onPeriodChange?: (value: PeriodValue) => void;
  onOpenTriage?: () => void;
  onOpenGap?: (id: string) => void;
  onResumeGap?: (id: string) => void;
  onViewAllLogs?: () => void;
}

const SEGMENT_COLORS: Record<GfSource, string> = {
  cancelled: 'var(--ai-3)',
  'no-show': 'var(--terracotta)',
  'short-gap': 'var(--gold)',
  'never-booked': 'var(--success)',
  'added-by-staff': 'var(--ink-4)',
};

/**
 * Gap Filler — the agent page (amendment 26 §2, board `GF3Agent`).
 *
 * **Working now sits above the numbers**: a gap that starts in three hours is the
 * most time-critical thing in AI Team, so the live gaps come first, soonest
 * first, and the 30-day dashboard follows.
 *
 * Every figure reconciles to the Dashboard's own 74 filled / 4 today — the source
 * table, the channels and the hero segments all add back to them (see the data
 * module), and today's four fills add to $3,850.
 */
export function GapFillerView({
  onHold,
  onAddGap,
  onOpenSettings,
  onSetHold,
  period,
  onPeriodChange,
  onOpenTriage,
  onOpenGap,
  onResumeGap,
  onViewAllLogs,
}: GapFillerViewProps) {
  const [asking, setAsking] = useState(false);
  const d = GAP_FILLER;
  const gaps = liveGaps();
  const openCount = gaps.length;
  const banner = gapTriageBanner();

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1084px] flex-1 overflow-y-auto px-[30px] pb-16 pt-5">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="pc-eyebrow text-[var(--ink-3)]">
            {openCount} open · {onHold ? 'on hold' : 'working'}
          </div>
          <h1 className="m-0 mt-0.5 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            {d.titleLead} <span className="text-[var(--ai-3)]">{d.titleAccent}</span>
          </h1>
          <p className="m-0 mt-0.5 text-[12.5px] text-[var(--ink-3)]">{d.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {period && onPeriodChange ? (
            <PeriodSelector variant="agent" value={period} onChange={onPeriodChange} startedISO="2026-03-03" />
          ) : null}
          <Button variant="primary" size="sm" iconLeft="plus" onClick={onAddGap}>
            Add a gap
          </Button>
          <Button variant="secondary" size="sm" iconLeft="cog" onClick={onOpenSettings}>
            Settings
          </Button>
          {onHold ? (
            <Button variant="primary" size="sm" iconLeft="play" onClick={() => { onSetHold?.(false); }}>
              Resume
            </Button>
          ) : (
            <Button variant="danger" size="sm" iconLeft="pause" onClick={() => { setAsking(true); }}>
              Hold
            </Button>
          )}
        </div>
      </div>

      {/* Triage banner — only when there is a task (§2). */}
      {banner !== null ? (
        <section className="mt-4 flex flex-wrap items-center gap-3 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--gold)_35%,transparent)] bg-[var(--gold-tint)] px-4 py-2.5">
          <Icon kind="phone-out" size={15} stroke={2} style={{ color: 'var(--gold)' }} />
          <span className="text-[13px] font-semibold text-[var(--gold)]">
            {banner.count} needs you
          </span>
          <span className="text-[12px] text-[var(--ink-2)]">{banner.text}</span>
          <button
            type="button"
            className="ml-auto text-[12.5px] font-semibold text-[var(--gold)] hover:underline"
            onClick={onOpenTriage}
          >
            Open Triage →
          </button>
        </section>
      ) : null}

      {onHold ? (
        <section className="mt-4 flex flex-wrap items-center gap-3 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--terracotta)_35%,transparent)] bg-[var(--cream-0)] px-4 py-2.5">
          <Icon kind="pause" size={15} stroke={2} style={{ color: 'var(--terracotta)' }} />
          <span className="text-[13px] font-semibold text-[var(--terracotta)]">Gap Filler is on hold</span>
          <span className="text-[12px] text-[var(--ink-2)]">Nothing is offered until you resume.</span>
        </section>
      ) : null}

      {/* today strip */}
      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-3.5 shadow-[var(--sh-1)]">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--ink-4)]">Today</div>
        {d.today.map((p) => (
          <span key={p.label} className="inline-flex items-baseline gap-1.5">
            <span className="font-[var(--font-mono)] text-[16px] font-bold text-[var(--ink-0)]">{p.value}</span>
            <span className="text-[12px] text-[var(--ink-3)]">{p.label}</span>
          </span>
        ))}
      </div>

      {/* working now — above the numbers, soonest first */}
      <div className="mt-5">
        <div className="flex items-baseline gap-2.5">
          <h2 className="m-0 text-[14px] font-bold text-[var(--ink-0)]">Working now</h2>
          <span className="text-[12px] text-[var(--ink-4)]">Soonest first.</span>
        </div>
        <div className="mt-2.5 grid gap-3 lg:grid-cols-2">
          {gaps.map((g) => (
            <LiveGapCard
              key={g.id}
              gap={g}
              onOpen={() => { onOpenGap?.(g.id); }}
              onResume={() => { onResumeGap?.(g.id); }}
            />
          ))}
        </div>
      </div>

      {/* hero */}
      <div className="mt-5 rounded-[var(--r-lg)] border border-l-[3px] border-[var(--cream-2)] border-l-[var(--brand-indigo)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]">
        <div className="pc-eyebrow text-[var(--ink-3)]">Booked into gaps</div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="font-[var(--font-display)] text-[32px] font-semibold tracking-[-0.03em] text-[var(--ink-0)]">
            {d.hero.booked}
          </span>
          <span className="text-[13px] text-[var(--ink-3)]">{d.hero.caption}</span>
        </div>
        <HeroBar segments={d.hero.segments} />
        <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1.5">
          {d.hero.segments.map((seg) => (
            <span key={seg.source} className="inline-flex items-center gap-1.5 text-[11.5px]">
              <span className="h-2 w-2 rounded-[2px]" style={{ background: SEGMENT_COLORS[seg.source] }} />
              <span className="text-[var(--ink-3)]">{GF_SOURCE_LABEL[seg.source]}</span>
              <span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{seg.value}</span>
            </span>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-baseline gap-x-6 gap-y-1 border-t border-[var(--cream-2)] pt-2 text-[12px] text-[var(--ink-3)]">
          <span>
            Per gap filled →{' '}
            <span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{d.hero.perGap}</span>
          </span>
          <span>
            Chair time filled →{' '}
            <span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{d.hero.chairTime}</span>
          </span>
          <span className="text-[var(--ink-4)]">Production, from appointment-reason prices</span>
        </div>
      </div>

      {/* tiles */}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {d.tiles.map((t) => (
          <div key={t.key} className="rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]">
            <div className="pc-eyebrow text-[var(--ink-3)]">{t.label}</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-[var(--font-display)] text-2xl font-semibold tracking-[-0.03em] text-[var(--ink-0)]">
                {t.value}
              </span>
              {t.delta !== undefined ? (
                <span className="text-[11.5px] font-semibold text-[var(--success)]">{t.delta}</span>
              ) : null}
            </div>
            <div className="mt-0.5 text-[11.5px] text-[var(--ink-4)]">{t.caption}</div>
          </div>
        ))}
      </div>

      {/* chart — bars only */}
      <Card className="mt-3">
        <SectionHeading n="" title="Gaps filled per week" sub="Last 13 weeks" />
        <WeekChart weeks={d.weeks} />
      </Card>

      {/* 01 where the gaps came from */}
      <Card className="mt-3">
        <SectionHeading n="01" title="Where the gaps came from" sub="" />
        <div className="grid grid-cols-[1.6fr_0.6fr_0.6fr_0.6fr] gap-x-3 px-1 pb-1 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.08em] text-[var(--ink-4)]">
          <span>Source</span>
          <span className="text-right">Found</span>
          <span className="text-right">Filled</span>
          <span className="text-right">Filled %</span>
        </div>
        {d.sourceRows.map((r) => (
          <div key={r.source} className="grid grid-cols-[1.6fr_0.6fr_0.6fr_0.6fr] items-center gap-x-3 border-t border-[var(--cream-2)] px-1 py-2 text-[12.5px]">
            <span className="font-semibold text-[var(--ink-0)]">{GF_SOURCE_LABEL[r.source]}</span>
            <span className="text-right font-[var(--font-mono)] text-[var(--ink-2)]">{r.found}</span>
            <span className="text-right font-[var(--font-mono)] text-[var(--ink-2)]">{r.filled}</span>
            <span className="text-right font-[var(--font-mono)] font-bold text-[var(--ink-0)]">
              {Math.round((r.filled / r.found) * 100)}%
            </span>
          </div>
        ))}
        {/* Totals, computed — the source rows add to the headline (§2). */}
        <div className="grid grid-cols-[1.6fr_0.6fr_0.6fr_0.6fr] items-center gap-x-3 border-t-2 border-[var(--cream-2)] px-1 py-2 text-[12.5px]">
          <span className="font-bold text-[var(--ink-0)]">Total</span>
          <span className="text-right font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{sum(d.sourceRows, 'found')}</span>
          <span className="text-right font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{sum(d.sourceRows, 'filled')}</span>
          <span className="text-right font-[var(--font-mono)] font-bold text-[var(--ink-0)]">
            {Math.round((sum(d.sourceRows, 'filled') / sum(d.sourceRows, 'found')) * 100)}%
          </span>
        </div>
      </Card>

      {/* 02 channels */}
      <Card className="mt-3">
        <SectionHeading n="02" title="Channels" sub="Which offer the patient said yes to" />
        {d.channels.map((ch) => (
          <div key={ch.label} className="flex items-baseline justify-between border-t border-[var(--cream-2)] px-1 py-2.5 text-[12.5px]">
            <span className="font-semibold text-[var(--ink-0)]">{ch.label}</span>
            <span className="text-[var(--ink-3)]">
              <span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{ch.booked}</span> of {ch.offers}{' '}
              offers ·{' '}
              <span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">
                {Math.round((ch.booked / ch.offers) * 100)}%
              </span>
            </span>
          </div>
        ))}
        <div className="border-t border-[var(--cream-2)] px-1 pt-2 text-[11.5px] text-[var(--ink-4)]">
          {sum(d.channels, 'booked')} booked in all.
        </div>
      </Card>

      {/* 03 recent gaps */}
      <Card className="mt-3">
        <div className="flex items-start justify-between">
          <SectionHeading n="03" title="Recent gaps" sub="Open one to see who was asked." />
          <button type="button" className="text-[12px] font-semibold text-[var(--ai-3)] hover:underline" onClick={onViewAllLogs}>
            View all logs →
          </button>
        </div>
        <div className="grid grid-cols-[1.6fr_1fr_0.9fr_1.3fr_0.5fr] gap-x-3 px-1 pb-1 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.08em] text-[var(--ink-4)]">
          <span>Gap</span>
          <span>Source</span>
          <span>Outcome</span>
          <span>Result</span>
          <span className="text-right">Booked</span>
        </div>
        {d.recent.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => { onOpenGap?.(r.id); }}
            className="grid w-full grid-cols-[1.6fr_1fr_0.9fr_1.3fr_0.5fr] items-center gap-x-3 border-t border-[var(--cream-2)] px-1 py-2 text-left text-[12px] hover:bg-[var(--cream-0)]"
          >
            <span className="truncate text-[var(--ink-1)]">{r.gap}</span>
            <span className="truncate text-[var(--ink-3)]">{GF_SOURCE_LABEL[r.source]}</span>
            <span><OutcomeChip outcome={r.outcome} /></span>
            <span className="truncate text-[var(--ink-3)]">{r.result}</span>
            <span className="text-right font-[var(--font-mono)] font-semibold text-[var(--ink-0)]">{r.booked}</span>
          </button>
        ))}
      </Card>

      {asking ? (
        <HoldModal
          onCancel={() => { setAsking(false); }}
          onConfirm={() => { setAsking(false); onSetHold?.(true); }}
        />
      ) : null}
    </div>
  );
}

function sum<T>(rows: T[], key: keyof T): number {
  return rows.reduce((n, r) => n + (r[key] as number), 0);
}

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]', className)}>
      {children}
    </div>
  );
}

function SectionHeading({ n, title, sub }: { n: string; title: string; sub: string }) {
  return (
    <div className="mb-2.5 flex flex-wrap items-baseline gap-2.5">
      {n !== '' ? <span className="font-[var(--font-mono)] text-[11px] font-bold text-[var(--ink-4)]">{n}</span> : null}
      <h2 className="m-0 text-[14px] font-bold text-[var(--ink-0)]">{title}</h2>
      <span className="text-[12px] text-[var(--ink-4)]">{sub}</span>
    </div>
  );
}

function HeroBar({ segments }: { segments: { source: GfSource; amount: number; value: string }[] }) {
  const total = segments.reduce((n, s) => n + s.amount, 0) || 1;
  return (
    <div className="mt-3 flex h-3 overflow-hidden rounded-full">
      {segments.map((seg) => (
        <div
          key={seg.source}
          style={{ width: `${String((seg.amount / total) * 100)}%`, background: SEGMENT_COLORS[seg.source] }}
          title={`${GF_SOURCE_LABEL[seg.source]}: ${seg.value}`}
        />
      ))}
    </div>
  );
}

function WeekChart({ weeks }: { weeks: { label: string; filled: number }[] }) {
  const W = 720;
  const H = 140;
  const padB = 18;
  const maxVal = Math.max(10, Math.ceil(weeks.reduce((n, w) => Math.max(n, w.filled), 0) / 5) * 5);
  const bw = W / weeks.length;
  const barW = bw * 0.5;
  const y = (v: number) => H - padB - (v / maxVal) * (H - padB - 6);
  return (
    <svg viewBox={`0 0 ${String(W)} ${String(H)}`} className="w-full" role="img" aria-label="Gaps filled per week">
      {[0, maxVal / 2, maxVal].map((t) => (
        <g key={t}>
          <line x1={0} x2={W} y1={y(t)} y2={y(t)} stroke="var(--cream-2)" strokeWidth={1} />
          <text x={2} y={y(t) - 2} fontSize={9} fill="var(--ink-4)" fontFamily="var(--font-mono)">{t}</text>
        </g>
      ))}
      {weeks.map((wk, i) => (
        <rect key={wk.label} x={i * bw + (bw - barW) / 2} y={y(wk.filled)} width={barW} height={H - padB - y(wk.filled)} rx={2} fill="color-mix(in srgb, var(--success) 55%, var(--cream-1))">
          <title>{`${wk.label}: ${String(wk.filled)} filled`}</title>
        </rect>
      ))}
      {weeks.map((wk, i) =>
        i % 2 === 0 ? (
          <text key={`x-${wk.label}`} x={i * bw + bw / 2} y={H - 4} fontSize={9} textAnchor="middle" fill="var(--ink-4)" fontFamily="var(--font-mono)">
            {wk.label}
          </text>
        ) : null,
      )}
    </svg>
  );
}

function Funnel({ funnel }: { funnel: GfFunnel }) {
  const boxes: [string, number][] = [
    ['eligible', funnel.eligible],
    ['offered', funnel.offered],
    ['replied', funnel.replied],
    ['booked', funnel.booked],
  ];
  return (
    <div className="flex gap-1.5">
      {boxes.map(([label, n]) => (
        <div
          key={label}
          className={cn(
            'flex-1 rounded-[8px] px-2 py-1.5 text-center',
            n === 0 ? 'bg-[var(--cream-0)] opacity-55' : 'bg-[var(--cream-0)]',
          )}
        >
          <div className="font-[var(--font-mono)] text-[15px] font-bold text-[var(--ink-0)]">{n}</div>
          <div className="text-[10px] text-[var(--ink-4)]">{label}</div>
        </div>
      ))}
    </div>
  );
}

function LiveGapCard({ gap, onOpen, onResume }: { gap: GfGap; onOpen: () => void; onResume: () => void }) {
  return (
    <div className="rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-3.5 shadow-[var(--sh-1)]">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13.5px] font-bold text-[var(--ink-0)]">{gap.when}</div>
          <div className="mt-0.5 text-[11.5px] text-[var(--ink-3)]">
            {GF_SOURCE_LABEL[gap.source]} · {gap.startsIn}
          </div>
        </div>
        {gap.state === 'paused' ? (
          <span className="shrink-0 rounded-[6px] bg-[#F1F3F9] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-[var(--ink-4)]">
            Paused
          </span>
        ) : null}
      </div>
      <div className="mt-1.5 text-[12px] text-[var(--ink-2)]">
        {gap.provider} · {gap.operatory} · {gap.minutes} min · {gap.fits}
      </div>
      <div className="mt-2.5">
        <Funnel funnel={gap.funnel} />
      </div>
      <div className="mt-2 text-[11px] leading-[1.55] text-[var(--ink-4)]">{gap.footer}</div>
      <div className="mt-2.5 flex gap-2">
        <Button variant="secondary" size="sm" onClick={onOpen}>View</Button>
        {gap.state === 'paused' ? (
          <Button variant="primary" size="sm" onClick={onResume}>Resume</Button>
        ) : null}
      </div>
    </div>
  );
}

function OutcomeChip({ outcome }: { outcome: GfOutcome }) {
  const tone: Record<GfOutcome, string> = {
    filled: 'var(--success)',
    'filled-by-practice': 'var(--ink-3)',
    expired: 'var(--terracotta)',
    'nobody-to-offer': 'var(--ink-4)',
  };
  return (
    <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold" style={{ color: tone[outcome] }}>
      {GF_OUTCOME_LABEL[outcome]}
    </span>
  );
}

function HoldModal({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(20,22,28,0.35)] px-4" role="dialog" aria-modal="true" aria-labelledby="hold-gap-title">
      <div className="w-full max-w-[420px] rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-5 shadow-[var(--sh-3)]">
        <h2 id="hold-gap-title" className="m-0 text-[16px] font-bold text-[var(--ink-0)]">Hold Gap Filler?</h2>
        <p className="m-0 mt-2 text-[13px] leading-[1.6] text-[var(--ink-2)]">
          No more offers go out for any gap until you resume. Replies are still answered, and anyone already booked keeps
          their time.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={onConfirm}>Hold</Button>
        </div>
      </div>
    </div>
  );
}

export { Funnel as GapFunnel };
export type { IconKind };
